/**
 * STORAGE CAPACITY CONFIGURATION
 * 
 * Centralized configuration for storage capacity validation
 */

/**
 * ALLOW_OVER_CAPACITY_RATIO - Soft limit for storage capacity
 * 
 * Allows upload to exceed capacity by a certain percentage before blocking
 * 
 * Value: 0.1 = 10%
 * 
 * Behavior:
 * - If (used + uploadSize) <= capacity * (1 + 0.1) → Allow upload (soft limit)
 * - If (used + uploadSize) > capacity * (1 + 0.1) → Block upload (hard limit)
 * 
 * Example:
 * - Capacity: 100GB
 * - Soft limit: 110GB (100 * 1.1)
 * - Current used: 95GB
 * - Upload 10GB → Total: 105GB → ALLOWED (within soft limit)
 * - Upload 20GB → Total: 115GB → BLOCKED (exceeds soft limit)
 */
export const ALLOW_OVER_CAPACITY_RATIO = 0.1; // 10%

/**
 * Calculate effective capacity with soft limit
 */
export const calculateEffectiveCapacity = (capacity: number): number => {
  return capacity * (1 + ALLOW_OVER_CAPACITY_RATIO);
};

/**
 * Check if upload would exceed soft limit
 */
export const wouldExceedSoftLimit = (
  currentUsage: number,
  uploadSize: number,
  capacity: number
): boolean => {
  const effectiveCapacity = calculateEffectiveCapacity(capacity);
  return (currentUsage + uploadSize) > effectiveCapacity;
};

/**
 * Calculate how much over the soft limit
 */
export const calculateExceededAmount = (
  currentUsage: number,
  uploadSize: number,
  capacity: number
): number => {
  const effectiveCapacity = calculateEffectiveCapacity(capacity);
  const totalAfterUpload = currentUsage + uploadSize;
  return Math.max(0, totalAfterUpload - effectiveCapacity);
};

/**
 * Calculate remaining capacity with soft limit
 */
export const calculateRemainingCapacity = (
  currentUsage: number,
  capacity: number
): number => {
  const effectiveCapacity = calculateEffectiveCapacity(capacity);
  return Math.max(0, effectiveCapacity - currentUsage);
};

/**
 * Format configuration info for logging/display
 */
export const getCapacityConfigInfo = () => ({
  allowOverCapacityRatio: ALLOW_OVER_CAPACITY_RATIO,
  allowOverCapacityPercent: `${ALLOW_OVER_CAPACITY_RATIO * 100}%`,
  description: `Uploads allowed up to ${ALLOW_OVER_CAPACITY_RATIO * 100}% over capacity (soft limit)`,
});
